import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cn } from '@/lib/utils';

/*
 * Avatar — DESIGN_SYSTEM.md §2.1
 * Three modes: image, initials (deterministic gradient), placeholder.
 * Optional status overlay (online/away dot).
 */

const sizeClasses = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-7 w-7 text-[11px]',
  md: 'h-9 w-9 text-xs',
  lg: 'h-12 w-12 text-sm',
} as const;

type AvatarSize = keyof typeof sizeClasses;

// 6 deterministic gradients (DESIGN_SYSTEM.md §1.1)
const gradientPalette = [
  'from-[#FBBF24] to-[#F59E0B]', // amber
  'from-[#A78BFA] to-[#7C3AED]', // violet
  'from-[#34D399] to-[#10B981]', // emerald
  'from-[#F472B6] to-[#EC4899]', // pink
  'from-[#FBBF24] to-[#10B981]', // sun
  'from-[#60A5FA] to-[#2563EB]', // ocean
  'from-[#FB923C] to-[#EF4444]', // sunset
  'from-[#22D3EE] to-[#3B82F6]', // sky
] as const;

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function gradientForId(id: string): string {
  return gradientPalette[hashString(id) % gradientPalette.length];
}

export function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> & { size?: AvatarSize }
>(({ className, size = 'md', ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn('relative flex shrink-0 overflow-hidden rounded-pill', sizeClasses[size], className)}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('aspect-square h-full w-full object-cover', className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn('flex h-full w-full items-center justify-center bg-surface-2 font-semibold text-soft', className)}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

/**
 * Initials fallback with deterministic gradient picked from the entity id.
 * Pair with `<Avatar>` and an optional `<AvatarImage>`:
 *
 *   <Avatar size="md">
 *     <AvatarImage src={user.photoUrl} />
 *     <AvatarInitials id={user.id} name={user.name} />
 *   </Avatar>
 */
const AvatarInitials = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  Omit<React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>, 'children'> & {
    id: string;
    name: string;
  }
>(({ className, id, name, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      'flex h-full w-full items-center justify-center bg-gradient-to-br font-semibold text-white',
      gradientForId(id),
      className,
    )}
    {...props}
  >
    {initialsFor(name)}
  </AvatarPrimitive.Fallback>
));
AvatarInitials.displayName = 'AvatarInitials';

interface AvatarStatusProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: 'available' | 'away' | 'busy';
}

const statusToneClass = {
  available: 'bg-status-available',
  away:      'bg-warn',
  busy:      'bg-danger',
} as const;

const AvatarStatus = React.forwardRef<HTMLSpanElement, AvatarStatusProps>(
  ({ className, tone = 'available', ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        'absolute bottom-0 right-0 h-2.5 w-2.5 rounded-pill ring-2 ring-surface',
        statusToneClass[tone],
        className,
      )}
      aria-hidden="true"
      {...props}
    />
  ),
);
AvatarStatus.displayName = 'AvatarStatus';

export { Avatar, AvatarImage, AvatarFallback, AvatarInitials, AvatarStatus };
